import { type BezierEvaluator, buildBezierEvaluator, totalLength } from '../bezier-geometry';
import { mod2pi, TAU } from '../guide-types';
import {
    autoWidthBodyExtent,
    autoWidthFillFactorFor,
    autoWidthScaleForPitch,
    invertAutoWidth,
    naturalWidthScale,
    spacingDivisor,
} from '../stitch-sizing';
import { defaultLegLength, isFullVariant, isLegCapType, type TbcPoint, type TbcType } from '../tbc-types';
import type { LegacyArrayTbc, WideTbc } from './types';

// SP-125. A frozen copy of where the renderer drew each instance of a stitch
// array, and where its chain-space curve ran, taken from array-tbc-types.ts
// before that file was deleted. The converter that turns a stored array into
// ordinary stitches reads it, so a converted chart is drawn where the array was.
// Don't "fix" or tidy the maths: test/legacy-arrays/geometry.test.ts holds it to
// what the array code answered, fixture by fixture.
//
// Copied as it was, minus the editing, hit-testing and gizmo code. The
// departures, none of which changes a number: the leg length is
// `defaultLegLength` itself (the old `arrayInstanceLegLength` only forwarded to
// it); the ring radius and the angle of a circle/arc space curve are their own
// functions, `legacySpaceRadius` and `legacySpaceAngleAt` (the converter builds
// the Space ring from them); and the memo caches are local to this file, keyed by
// object identity like the originals (sound while the array object isn't
// mutated, which the converter never does).
//
// Like the original, a line or bezier array with a fractional count AND
// autoWidth throws here: the original couldn't draw one either.

/** How many instances the renderer drew: every integer i with 0 <= i < count.
 *  A fractional count gets no extra instance — the renderer's loop reaches
 *  ceil(count) but clamped the last index out of range, so that one had no
 *  points and was never drawn. */
export function legacyInstanceCount(a: LegacyArrayTbc): number {
    return Number.isFinite(a.count) ? Math.max(0, Math.floor(a.count)) : 0;
}

/** The stitch type drawn at instance `i`: the combination entry at
 *  `i % combination.length` when a non-empty combination is present, else the
 *  uniform `stitchType`. */
function instanceStitchType(a: LegacyArrayTbc, i: number): TbcType {
    const combo = a.combination;
    if (combo == null || combo.length === 0) return a.stitchType;
    const n = combo.length;
    return combo[((i % n) + n) % n];
}

function memoized<T>(cache: WeakMap<LegacyArrayTbc, T>, a: LegacyArrayTbc, compute: () => T): T {
    if (cache.has(a)) return cache.get(a) as T;
    const value = compute();
    cache.set(a, value);
    return value;
}

const bezierEvaluatorCache = new WeakMap<LegacyArrayTbc, BezierEvaluator>();

/** The arc-length evaluator of a bezier array's base curve. Caller ensures
 *  `a.shape === 'bezier-curve'`. */
function bezierEvaluator(a: LegacyArrayTbc): BezierEvaluator {
    return memoized(bezierEvaluatorCache, a, () => {
        if (a.shape !== 'bezier-curve') throw new Error('bezierEvaluator: not a bezier array');
        return buildBezierEvaluator({ id: a.id, type: 'bezier-curve', anchors: a.anchors, closed: a.closed });
    });
}

const instancePointsCache = new WeakMap<LegacyArrayTbc, { tops: TbcPoint[]; bases: TbcPoint[] }>();

/** All derived instance endpoints of `a` (index i → instance i). */
function instancePoints(a: LegacyArrayTbc): { tops: readonly TbcPoint[]; bases: readonly TbcPoint[] } {
    return memoized(instancePointsCache, a, () => computeInstancePoints(a));
}

/** One-pass derivation behind {@link instancePoints}.
 *  - circle/arc: angle i·τ/N (circle) or endpoints-included across the CCW
 *    sweep (arc); tops/bases radial on their respective radii.
 *  - line: tangent/normal computed once; on-curve point lerped per fraction.
 *  - bezier: ONE arc-length evaluator, queried per fraction.
 *  Open shapes use fraction i/(N-1) (endpoints included); closed loops i/N.
 *  Normal points to the free-end side: flip=false → left-rotated tangent
 *  ((-ty, tx)), which is screen-down for a left-to-right line; the free end
 *  sits the stitch's leg along it. */
function computeInstancePoints(a: LegacyArrayTbc): { tops: TbcPoint[]; bases: TbcPoint[] } {
    const tops: TbcPoint[] = [];
    const bases: TbcPoint[] = [];
    if (a.shape === 'circle' || a.shape === 'arc') {
        // Circle is a closed loop; arc is open (endpoints included).
        const sweep = a.shape === 'arc' ? mod2pi(a.endAngle - a.startAngle) : TAU;
        const divisor = spacingDivisor(a.count, a.shape === 'circle');
        // The top sits one (height-scaled) leg radially OUTWARD from the base
        // ring. Folding the height factor into geometry (vs a render-time
        // heightScale) makes the top marker land on the rendered glyph top.
        const heightScale = arrayUniformHeightScale(a);
        for (let i = 0; i < a.count; i++) {
            const topRadius = a.baseRadius + defaultLegLength(instanceStitchType(a, i)) * heightScale;
            const ang = a.shape === 'circle'
                ? (i * sweep) / divisor
                : a.startAngle + (i * sweep) / divisor;
            const cos = Math.cos(ang);
            const sin = Math.sin(ang);
            tops.push({ x: a.center.x + topRadius * cos, y: a.center.y + topRadius * sin });
            bases.push({ x: a.center.x + a.baseRadius * cos, y: a.center.y + a.baseRadius * sin });
        }
        return { tops, bases };
    }
    const closed = a.shape === 'bezier-curve' && a.closed;
    // Height factor folds into geometry (uniform across instances) so the top
    // point equals the rendered glyph top — same contract as circle/arc.
    const legHeightScale = arrayUniformHeightScale(a);
    let pointAt: (fr: number) => TbcPoint;
    let tangentAt: (fr: number) => TbcPoint;
    if (a.shape === 'line') {
        const dx = a.end.x - a.start.x;
        const dy = a.end.y - a.start.y;
        const len = Math.hypot(dx, dy);
        const tangent: TbcPoint = len === 0 ? { x: 1, y: 0 } : { x: dx / len, y: dy / len };
        pointAt = fr => ({ x: a.start.x + dx * fr, y: a.start.y + dy * fr });
        tangentAt = () => tangent;
    } else {
        const ev = bezierEvaluator(a);
        pointAt = ev.pointAt;
        tangentAt = fr => ev.tangentAt(fr) ?? { x: 1, y: 0 };
    }
    const d = spacingDivisor(a.count, closed);
    for (let i = 0; i < a.count; i++) {
        const fr = i / d;
        const leg = defaultLegLength(instanceStitchType(a, i)) * legHeightScale;
        const onCurve = pointAt(fr);
        const tangent = tangentAt(fr);
        const normal = a.flip
            ? { x: tangent.y, y: -tangent.x }
            : { x: -tangent.y, y: tangent.x };
        const free: TbcPoint = {
            x: onCurve.x + normal.x * leg,
            y: onCurve.y + normal.y * leg,
        };
        if (a.snapSide === 'top') {
            tops.push(onCurve);
            bases.push(free);
        } else if (a.snapSide === 'midpoint') {
            // Midpoint snap side: the instance straddles the curve — top and
            // base sit half a leg to either side. The top takes the +normal
            // side; flip swaps the sides.
            tops.push({ x: onCurve.x + normal.x * (leg / 2), y: onCurve.y + normal.y * (leg / 2) });
            bases.push({ x: onCurve.x - normal.x * (leg / 2), y: onCurve.y - normal.y * (leg / 2) });
        } else {
            bases.push(onCurve);
            tops.push(free);
        }
    }
    return { tops, bases };
}

/** World position of instance `i`'s top. Out-of-range indices clamp to
 *  [0, count-1]. */
function instanceTopPoint(a: LegacyArrayTbc, i: number): TbcPoint {
    const idx = Math.max(0, Math.min(a.count - 1, i));
    return instancePoints(a).tops[idx];
}

/** World position of instance `i`'s base. Out-of-range indices clamp to
 *  [0, count-1]. */
function instanceBasePoint(a: LegacyArrayTbc, i: number): TbcPoint {
    const idx = Math.max(0, Math.min(a.count - 1, i));
    return instancePoints(a).bases[idx];
}

/** Gap measured FROM THE TOPS to the space guide (topward / outward). The
 *  explicit per-array value when set, else one height-scaled stitch leg. */
function resolvedSpaceOffset(a: LegacyArrayTbc): number {
    return a.spaceOffset ?? defaultLegLength(a.stitchType) * arrayUniformHeightScale(a);
}

/** The radius of a circle or arc array's chain-space curve: beyond the instance
 *  tops (at `baseRadius` + the height-scaled leg) by the space offset. */
export function legacySpaceRadius(a: LegacyArrayTbc & { shape: 'circle' | 'arc' }): number {
    const leg = defaultLegLength(a.stitchType) * arrayUniformHeightScale(a);
    const topRadius = a.baseRadius + leg;
    return topRadius + resolvedSpaceOffset(a);
}

/** The angle at which a circle or arc array's chain-space curve passes
 *  fraction `f` ∈ [0,1] (clamped): `f` of the way round the sweep, from 0 on a
 *  circle and from `startAngle` on an arc. The ring point there is
 *  `center + legacySpaceRadius(a)·(cos, sin)` of it, which is
 *  {@link legacySpacePointAt}. */
export function legacySpaceAngleAt(a: LegacyArrayTbc & { shape: 'circle' | 'arc' }, f: number): number {
    const fr = Math.max(0, Math.min(1, f));
    const sweep = a.shape === 'arc' ? mod2pi(a.endAngle - a.startAngle) : TAU;
    return a.shape === 'circle' ? fr * sweep : a.startAngle + fr * sweep;
}

/** World point on the array's chain-space curve at fraction `f` ∈ [0,1].
 *
 *  The curve is anchored to the array's TOP points (the free ends of the
 *  drawn stitches) and offset further outward / topward by the space offset.
 *
 *  ### line / bezier
 *  Let `onCurve` be the point on the base curve at `f`, `normal` the flip-
 *  aware perpendicular (flip ? (ty,−tx) : (−ty,tx)), and
 *  `leg = defaultLegLength(stitchType) * heightScale`.
 *  - snapSide 'top':      topPoint = onCurve,              topward = −normal
 *  - snapSide 'base':     topPoint = onCurve + normal·leg, topward = +normal
 *  - snapSide 'midpoint': topPoint = onCurve + normal·(leg/2), topward = +normal
 *  Returns `topPoint + topward * offset`.
 *
 *  ### circle / arc
 *  The point at the angle `f` of the way round the sweep, on {@link legacySpaceRadius}.
 *
 *  `f` is clamped to [0,1]. */
export function legacySpacePointAt(a: LegacyArrayTbc, f: number): TbcPoint {
    const fr = Math.max(0, Math.min(1, f));
    if (a.shape === 'circle' || a.shape === 'arc') {
        const ang = legacySpaceAngleAt(a, fr);
        const r = legacySpaceRadius(a);
        return { x: a.center.x + r * Math.cos(ang), y: a.center.y + r * Math.sin(ang) };
    }
    const offset = resolvedSpaceOffset(a);
    // Height-scaled leg — same expression used in computeInstancePoints for tops.
    const leg = defaultLegLength(a.stitchType) * arrayUniformHeightScale(a);
    let onCurve: TbcPoint;
    let tangent: TbcPoint;
    if (a.shape === 'line') {
        const dx = a.end.x - a.start.x;
        const dy = a.end.y - a.start.y;
        const len = Math.hypot(dx, dy);
        tangent = len === 0 ? { x: 1, y: 0 } : { x: dx / len, y: dy / len };
        onCurve = { x: a.start.x + dx * fr, y: a.start.y + dy * fr };
    } else {
        const ev = bezierEvaluator(a);
        onCurve = ev.pointAt(fr);
        tangent = ev.tangentAt(fr) ?? { x: 1, y: 0 };
    }
    const normal = a.flip ? { x: tangent.y, y: -tangent.x } : { x: -tangent.y, y: tangent.x };
    // Compute topPoint (actual top location) and topward unit per snapSide.
    // snapSide 'top':      tops are ON the curve; topward = −normal (into the free-end side).
    // snapSide 'base':     tops are at onCurve + normal*leg; topward = +normal.
    // snapSide 'midpoint': tops are at onCurve + normal*(leg/2); topward = +normal.
    let topPoint: TbcPoint;
    let topwardX: number;
    let topwardY: number;
    if (a.snapSide === 'top') {
        topPoint = onCurve;
        topwardX = -normal.x;
        topwardY = -normal.y;
    } else if (a.snapSide === 'midpoint') {
        topPoint = { x: onCurve.x + normal.x * (leg / 2), y: onCurve.y + normal.y * (leg / 2) };
        topwardX = normal.x;
        topwardY = normal.y;
    } else {
        // 'base'
        topPoint = { x: onCurve.x + normal.x * leg, y: onCurve.y + normal.y * leg };
        topwardX = normal.x;
        topwardY = normal.y;
    }
    return {
        x: topPoint.x + topwardX * offset,
        y: topPoint.y + topwardY * offset,
    };
}

/** A minimal 1-base-1-top Tbc view model for instance `i`: what the array
 *  drew there. Ids are synthetic (`<arrayId>:<i>`, `:b<i>`, `:t<i>`). */
export function legacyInstanceViewModel(a: LegacyArrayTbc, i: number): WideTbc {
    // Auto width (fill guide) overrides the stored widthScale; null falls
    // back to the stored value / type default.
    const autoWs = arrayAutoWidthScaleAt(a, i);
    const effectiveWidthScale = autoWs ?? a.widthScale;
    // Height is baked into the instance geometry (see computeInstancePoints),
    // so instances render at the default heightScale (1) — the top point IS the
    // drawn top.
    //
    // The renderer derives BOTH glyph axes from b = |top − base| (width =
    // b · widthScale). Because the height factor `s` is folded into the
    // geometry, b = leg · s, which would otherwise make the rendered WIDTH
    // scale with height. Divide the emitted widthScale by `s` so it cancels:
    // width = b · (widthScale / s) = leg · widthScale — independent of height
    // (so unlocked height changes don't widen the glyph), while a locked aspect
    // ratio stays constant at every size.
    const s = arrayUniformHeightScale(a);
    const renderWidthScale = effectiveWidthScale != null && s > 0
        ? effectiveWidthScale / s
        : effectiveWidthScale;
    return {
        id: `${a.id}:${i}`,
        type: instanceStitchType(a, i),
        bases: [{ id: `${a.id}:b${i}`, point: instanceBasePoint(a, i), snap: null }],
        tops: [{ id: `${a.id}:t${i}`, point: instanceTopPoint(a, i), snap: null }],
        ...(renderWidthScale != null ? { widthScale: renderWidthScale } : {}),
        ...(a.color != null ? { color: a.color } : {}),
        ...(a.strokeWidthScale != null ? { strokeWidthScale: a.strokeWidthScale } : {}),
        ...(a.strokeCap != null ? { strokeCap: a.strokeCap } : {}),
    };
}

/** Derived heightScale while the aspect ratio is locked: the effective
 *  widthScale (auto-width when on, manual otherwise) over the type's natural
 *  ratio. Undefined when there is no effective widthScale to track (auto off
 *  and no manual value), leaving the array at natural height. */
function lockedHeightScale(type: TbcType, effectiveWidth: number | null): number | undefined {
    if (effectiveWidth == null) return undefined;
    return effectiveWidth / naturalWidthScale(type);
}

/** The single effective height scale: the manual value when unlocked, the
 *  uniform locked-derived value when locked. 1 = natural height. */
function arrayUniformHeightScale(a: LegacyArrayTbc): number {
    if (a.aspectRatioLocked === false) return a.heightScale ?? 1;
    return lockedHeightScale(a.stitchType, arrayAutoWidthScale(a) ?? a.widthScale ?? null) ?? 1;
}

const autoWidthCache = new WeakMap<LegacyArrayTbc, number | null>();

/** Effective widthScale for auto-width mode, or null when off / not
 *  computable. Width only: the symbol's rendered width becomes
 *  pitch · {@link autoWidthFillFactorFor} (curve length per instance, minus
 *  a proportional breathing gap — except 'chain', which tiles at factor 1
 *  with no gap). The height (leg / ring gap) is untouched, and instance
 *  POSITIONS never change.
 *
 *  Circle/arc pitch is measured at the SYMBOL-CENTER radius: the base ring
 *  for midpoint variants (their bounding box centers at the base), the
 *  midpoint between rings for -full variants. */
function arrayAutoWidthScale(a: LegacyArrayTbc): number | null {
    if (a.autoWidth !== true) return null;
    return memoized(autoWidthCache, a, () => computeAutoWidthScale(a));
}

function computeAutoWidthScale(a: LegacyArrayTbc): number | null {
    const type = a.stitchType;
    const full = isFullVariant(type);

    // b source: the |base→top| extent the width formula is expressed against.
    let baseToTop: number;
    let pitch: number;
    if (a.shape === 'circle' || a.shape === 'arc') {
        // Body extent is the per-type leg (same as line/bezier). Pitch radius is
        // the symbol-center radius: the base ring for midpoint variants, base +
        // leg/2 for -full variants. Uses the natural (unscaled) leg so this
        // stays independent of the height factor and the auto-width <->
        // aspect-lock derivation cannot become circular.
        baseToTop = defaultLegLength(type);
        const r = full ? a.baseRadius + defaultLegLength(type) / 2 : a.baseRadius;
        const sweep = a.shape === 'circle' ? TAU : mod2pi(a.endAngle - a.startAngle);
        const arcLen = sweep * r;
        pitch = arcLen / spacingDivisor(a.count, a.shape === 'circle');
    } else {
        baseToTop = defaultLegLength(type);
        let length: number;
        if (a.shape === 'line') {
            length = Math.hypot(a.end.x - a.start.x, a.end.y - a.start.y);
        } else {
            length = totalLength({ id: a.id, type: 'bezier-curve', anchors: a.anchors, closed: a.closed });
        }
        const closed = a.shape === 'bezier-curve' && a.closed;
        pitch = length / spacingDivisor(a.count, closed);
    }
    return autoWidthScaleForPitch(type, pitch, baseToTop);
}

const perInstanceAutoWidthCache = new WeakMap<LegacyArrayTbc, ReadonlyArray<number | null>>();

/** Per-instance effective widthScale for auto-width mode — the
 *  curvature-aware refinement of {@link arrayAutoWidthScale} for OPEN
 *  curves. Symbols are not centered ON the curve: a midpoint variant's
 *  symbol centers at its base, a -full variant's at the leg midpoint —
 *  both ride an offset rail whose local spacing on a bent curve is
 *  pitch·(1 ± offset·curvature). Here each instance's target width is the mean
 *  distance to its NEIGHBORS' symbol centers (trend-extrapolated at open
 *  endpoints; wrap-around for closed beziers). Circle/arc (constant curvature
 *  — already measured at the symbol-center radius) delegate to the uniform
 *  value. */
function arrayAutoWidthScaleAt(a: LegacyArrayTbc, i: number): number | null {
    if (a.autoWidth !== true) return null;
    if (a.shape === 'circle' || a.shape === 'arc') return arrayAutoWidthScale(a);
    const scales = memoized(perInstanceAutoWidthCache, a, () => computePerInstanceAutoWidthScales(a));
    return scales[Math.max(0, Math.min(a.count - 1, i))] ?? null;
}

function computePerInstanceAutoWidthScales(a: Extract<LegacyArrayTbc, { shape: 'line' | 'bezier-curve' }>): Array<number | null> {
    const type = a.stitchType;
    if (isLegCapType(type)) return new Array<number | null>(a.count).fill(null);
    if (a.count <= 1) return [arrayAutoWidthScale(a)];
    const full = isFullVariant(type);
    const bodyExtent = autoWidthBodyExtent(type, defaultLegLength(type));
    const fill = autoWidthFillFactorFor(type);
    // Symbol centers: base for midpoint variants, leg midpoint for -full —
    // the same convention as the circle/arc pitch radius above.
    const centers: TbcPoint[] = [];
    for (let k = 0; k < a.count; k++) {
        const bp = instanceBasePoint(a, k);
        if (!full) {
            centers.push(bp);
            continue;
        }
        const tp = instanceTopPoint(a, k);
        centers.push({ x: (bp.x + tp.x) / 2, y: (bp.y + tp.y) / 2 });
    }
    const n = a.count;
    const closed = a.shape === 'bezier-curve' && a.closed;
    const gapCount = closed ? n : n - 1;
    const gaps: number[] = [];
    for (let k = 0; k < gapCount; k++) {
        const p = centers[k];
        const q = centers[(k + 1) % n];
        gaps.push(Math.hypot(q.x - p.x, q.y - p.y));
    }
    const out: Array<number | null> = [];
    for (let k = 0; k < n; k++) {
        const prev = closed ? gaps[(k - 1 + n) % n] : k > 0 ? gaps[k - 1] : null;
        const next = closed ? gaps[k] : k < n - 1 ? gaps[k] : null;
        // Open endpoints: linearly extrapolate the spacing trend so the end
        // PAIRS tile exactly — a plain single-neighbor width leaves the end
        // pair off by (g1−g0)/4. Floored at half the adjacent gap so a wild
        // trend can't collapse the end symbol (or go negative).
        const local = prev != null && next != null ? (prev + next) / 2
            : next != null ? Math.max(next + (next - (gaps[1] ?? next)) / 2, next / 2)
            : prev != null ? Math.max(prev + (prev - (gaps[gaps.length - 2] ?? prev)) / 2, prev / 2)
            : null;
        out.push(local == null ? null : invertAutoWidth(type, local * fill, bodyExtent));
    }
    return out;
}
